import { ProfileType } from "../__generated__/graphql";
import {
    MissionInfoFieldsFragment,
    MissionPayrollBreakdownFieldsFragment,
} from "../__generated__/operations/graphql";
import React, { use, useMemo } from "react";
import ListItem from "../ui_atoms/ListItem";
import { formatActivityTypeTitle, formatWorkingTimeClassicSpec } from "../utils";
import { ProfileSwitcherManagerContext } from "../contexts/ProfileSwitcherManager";
import { useI18n } from "../hooks/i18n-hooks";
import { usePayrollBreakdownFormatter } from "../hooks/payroll-beakdown-utils";
import { useIntercom } from "../hooks/intercom";
import { ColorValue, StyleSheet, View } from "react-native";
import { XOR } from "ts-essentials";

type MissionInfoItemProps = { backgroundColor?: ColorValue } & XOR<
    {
        payrollBreakdown: MissionPayrollBreakdownFieldsFragment;
    },
    {
        missionInfo: MissionInfoFieldsFragment;
    }
>;

const MissionInfoItem: React.FC<MissionInfoItemProps> = ({
    backgroundColor,
    payrollBreakdown,
    missionInfo = payrollBreakdown?.missionInfo,
}) => {
    const { profileType } = use(ProfileSwitcherManagerContext);
    const { formatPayrollDuration } = usePayrollBreakdownFormatter();
    const { presentArticleId } = useIntercom();

    const i18n = useI18n();

    const periodDescription = useMemo(() => {
        if (!payrollBreakdown) {
            return "";
        }
        return i18n.t("screens.payroll_breakdown.tracking_dates", {
            period: formatPayrollDuration(payrollBreakdown),
        });
    }, [payrollBreakdown, formatPayrollDuration, i18n]);

    return (
        <View style={{ backgroundColor }}>
            {profileType === ProfileType.Client && (
                <ListItem
                    avatarPath={missionInfo?.workerInfo?.avatarUrl}
                    description={missionInfo?.workerInfo?.name}
                    style={backgroundColor && styles.item}
                />
            )}
            {profileType === ProfileType.Worker && (
                <ListItem
                    avatarPath={missionInfo?.clientInfo?.logoUrl}
                    description={missionInfo?.clientInfo?.name}
                    style={backgroundColor && styles.item}
                />
            )}
            <ListItem
                icon="tag"
                description={
                    missionInfo?.name ||
                    (missionInfo?.activityType?.title
                        ? formatActivityTypeTitle(
                              missionInfo?.activityType?.title,
                              missionInfo?.workerInfo?.gender,
                          )
                        : "")
                }
                style={backgroundColor && styles.item}
            />
            {missionInfo?.workingTimeClassicSpec && (
                <ListItem
                    icon="clock"
                    description={formatWorkingTimeClassicSpec(
                        missionInfo.workingTimeClassicSpec,
                        i18n,
                    )}
                    style={backgroundColor && styles.item}
                />
            )}
            {payrollBreakdown && (
                <ListItem
                    icon="calendar-range"
                    description={periodDescription}
                    labels={[
                        {
                            mode: "miniCtaBold",
                            text: i18n.t("know_more"),
                            onPress: () => {
                                void presentArticleId("10704268");
                            },
                        },
                    ]}
                    style={backgroundColor && styles.item}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    item: { borderBottomColor: "white" },
});

export default MissionInfoItem;
